import { useContext, useRef } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import { GetTokenSilentlyOptions, useAuth0 } from "@auth0/auth0-react";
import jwtDecode from 'jwt-decode';
import { useIncognitoCheck } from './useIncognito';
import { useSnackbar } from 'notistack';
import { usePopupCheck } from './usePopupCheck';

const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) throw new Error('Auth context must be use inside AuthProvider');

  return context;
};

export default useAuth;

/**
 * This hook provides a function to get an API token for the specified audience.
 * It uses Auth0's `getAccessTokenSilently` method to retrieve the token
 * and handles errors by prompting the user to log in if necessary.
 * 
 * TODO: Once everything moves onto the same domain, we can simplify this significantly.
 * This has been temporarily made more complex to handle multiple audiences and incognito mode.
 * @returns { getApiToken: (tokenOptions: GetTokenSilentlyOptions) => Promise<string | undefined> }
*/
type UseGetApiTokenResult = {
  getApiToken: (tokenOptions: GetTokenSilentlyOptions, reloginOnFailure: boolean) => Promise<string | undefined>;
};

export function useGetApiToken(): UseGetApiTokenResult {
  const { getAccessTokenSilently, loginWithRedirect, getAccessTokenWithPopup, isAuthenticated } = useAuth0();
  const tokenCache = useRef<{ [aud: string]: { token: string | undefined, exp: number } }>({});
  const checkIncognito = useIncognitoCheck();
  const { checkPopupsOnce } = usePopupCheck();
  const { enqueueSnackbar } = useSnackbar();
  const primaryAudience = process.env.REACT_APP_HOST_API_URL ?? 'default';
  let queue = Promise.resolve<string | void>(undefined);

  const getApiToken = (tokenOptions: GetTokenSilentlyOptions, reloginOnFailure: boolean) => {

    queue = queue.then(async () => {
      const audience = tokenOptions?.authorizationParams?.audience ?? primaryAudience;
      const isPrimary = audience?.toLocaleLowerCase() === primaryAudience?.toLocaleLowerCase();
      const { isPrivate, browserName } = await checkIncognito();
      if (isPrivate || browserName?.toLocaleLowerCase() === 'safari') {
        const popupsEnabled = await checkPopupsOnce();
        if (!popupsEnabled && !isPrimary) {
          console.warn(`Browser ${browserName} is in incognito/private mode (or using Safari with ITP) and popups are blocked, redirecting to login.`);
          enqueueSnackbar('Please enable popups for this site, to enable full access to application!', {
            variant: 'warning',
            autoHideDuration: 10000
          });
          return reloginOnFailure ? loginWithRedirect(tokenOptions).then(() => undefined) : Promise.resolve(undefined);
        }
      }
      try {
        const cached = tokenCache.current[audience];
        const now = Math.floor(Date.now() / 1000);
        if (cached && cached.exp > (now + 60)) { // 60s buffer
          return cached.token;
        }

        const tokenResult = await getAccessTokenSilently(tokenOptions);
        const token = typeof tokenResult === "string" ? tokenResult : undefined;
        if (!token) {
          throw new Error("Failed to retrieve a valid access token for audience:" + audience);
        }
        // Decode JWT to get exp
        const { exp } = jwtDecode<{ exp: number }>(token);
        tokenCache.current[audience] = { token, exp };
        return token;
      } catch (e: any) {
        console.warn("Silent token retrieval failed attempting to retrieve from cache:", e, "for audience:", audience, "isAuthenticated:", isAuthenticated);
        // Try fallback cache if available and not expired
        const cached = tokenCache.current[audience];
        const now = Math.floor(Date.now() / 1000);
        if (cached && cached.exp > (now + 60)) { // 60s buffer
          console.warn("Token retrieved from Cache:", e, "for audience:", audience, "isAuthenticated:", isAuthenticated);
          return cached.token;
        }

        // Interactive fallback
        const needsInteraction = ['login_required', 'consent_required', 'interaction_required', 'missing_refresh_token'].includes(e?.error);
        if (needsInteraction) {
          let token: string | undefined = undefined;
          if (isPrivate || browserName?.toLocaleLowerCase() === 'safari') {
            const popupsEnabled = await checkPopupsOnce();

            if (!popupsEnabled && !isPrimary) {
              console.warn(`Browser ${browserName} is in incognito/private mode (or using Safari with ITP) and popups are blocked, redirecting to login.`);
              enqueueSnackbar('Please enable popups for this site, to enable full access to application!', {
                variant: 'warning',
                autoHideDuration: 10000,
                key: "popup-error",
                preventDuplicate: true
              });

              if (reloginOnFailure) {
                await loginWithRedirect(tokenOptions);
                return undefined;
              }
            }
            console.warn(`Browser ${browserName} is in incognito/private mode (or using Safari with ITP), using popup for authentication.`);
            try {
              token = await getAccessTokenWithPopup(tokenOptions) as string | undefined;
              const { exp } = jwtDecode<{ exp: number }>(token as string);
              if (token) {
                tokenCache.current[audience] = { token: token, exp };
                return token;
              }
            }
            catch (popupError) {
              console.error("Failed to retrieve a valid access token from popup for audience:" + audience, popupError);
              // Popup failed, show a notification to the user to enable popups (at a minimum) and if possible enable cookies.
              enqueueSnackbar('Please enable popups for this site!', {
                variant: 'error',
                autoHideDuration: 10000,
                key: "popup-error",
                preventDuplicate: true
              });

              // wait 10 seconds to allow user to read the message before redirecting to login
              await new Promise(res => setTimeout(res, 10000));
              if (reloginOnFailure) {
                await loginWithRedirect(tokenOptions);
              }
            }
          }
        }
        console.error("User will be re-logged in --> Failed to retrieve a valid access token from popup for audience:" + audience);
        if (reloginOnFailure) {
          await loginWithRedirect(tokenOptions);
        }
        return undefined;
      }
    });
    return queue as Promise<string | undefined>;
  };

  return { getApiToken };
}
